import {
  DM_BUTTON_SALON_PAGE_UNAVAILABLE_MESSAGES,
  type DmButtonFallback,
  type DmButtonSalonPageKind,
  type DmButtonSalonPageUnavailableReason,
} from '@repo/shared/marketing';
import type { GetMarketingCommentAutomationsId200Data } from '@/api/generated/schemas';
import type { MetaConnection } from '@/features/integrations/facebook-instagram/meta-connection-copy';

/**
 * Plain-words copy for Marketing › Comment → DM automations. Every API reason code the automation lifecycle
 * can return maps here, so screens never show a raw code. Connection copy lives with the Facebook &
 * Instagram integration (`meta-connection-copy`).
 */

/** One automation as the editor and every mutation return it (list rows add their period `results`). */
export type CommentAutomation = GetMarketingCommentAutomationsId200Data;

/** Post dates share the connection's short date format ("Sep 12"). */
export { formatShortDate } from '@/features/integrations/facebook-instagram/meta-connection-copy';

/** Pause / resume / delete failures → toast copy. */
const LIFECYCLE_FAILURE_COPY: Record<string, string> = {
  META_NOT_CONNECTED: 'Connect your Facebook Page first, then turn this automation on.',
  META_RECONNECT_REQUIRED: 'Reconnect Facebook first, then turn this automation on.',
  INSTAGRAM_NOT_LINKED: 'Link your Instagram account to the Facebook Page and reconnect, then turn this on.',
  POST_NOT_ON_CONNECTED_PAGE: 'This post belongs to a Page or Instagram account that’s no longer connected. Pick a new post.',
};

export function describeLifecycleFailure(reason: string | null, fallback: string): string {
  return (reason ? LIFECYCLE_FAILURE_COPY[reason] : undefined) ?? fallback;
}

/**
 * Warning for an automation whose post can't be served by the current connection. A Facebook post id is
 * `{pageId}_{postId}`, so a Page switch is detectable here; for Instagram only a missing IG link is
 * (a different IG account is caught by the API when the automation is resumed).
 */
export function automationPostWarning(automation: CommentAutomation, connection: MetaConnection | null | undefined): string | null {
  if (!connection) return null;
  if (automation.platform === 'FACEBOOK') {
    return automation.post.postId.startsWith(`${connection.pageId}_`) ? null : 'Post is on a previously connected Page';
  }
  return connection.ig ? null : 'Instagram isn’t linked any more';
}

/**
 * True while an automatic row waits for the worker to read its post (caption, thumbnail, link). The webhook creates
 * the row with no permalink; the first action run fills it in.
 */
export function autoRowPostPending(automation: Pick<CommentAutomation, 'source' | 'post'>): boolean {
  return automation.source === 'AUTO' && automation.post.permalink === null;
}

/**
 * The worker reads a new post at once and the sweep retries up to a day later, then stops. Past this age a row still
 * without its post's details won't get them (post deleted, connection broken), so it stops saying "Loading".
 */
export const AUTO_ROW_POST_PENDING_MAX_MS = 25 * 60 * 60 * 1000;

/**
 * Subline of an automatic row in the list, or null for a row the team set up by hand. A row that still follows the
 * automatic reply changes when that reply is saved; one edited (or paused) by itself keeps its own setup.
 */
export function autoRowSubline(
  automation: Pick<CommentAutomation, 'source' | 'followsDefault' | 'post' | 'createdAt'>,
  now: Date = new Date()
): string | null {
  if (automation.source !== 'AUTO') return null;
  if (autoRowPostPending(automation)) {
    const age = now.getTime() - new Date(automation.createdAt).getTime();
    return age < AUTO_ROW_POST_PENDING_MAX_MS ? 'Loading post…' : 'Couldn’t read this post';
  }
  return automation.followsDefault ? 'Follows the automatic reply' : 'Changed for this post';
}

/** "92% of comments" — null when the base is zero (nothing to compare against yet). */
export function percentOf(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}

export const PLATFORM_LABEL = { FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram' } as const;

const PAGE_DOWN_WORDS: Record<DmButtonSalonPageKind, string> = {
  NEW_CLIENT_OFFER: 'Your New Client Offer is paused',
  BIO_PAGE: 'Your Bio Page isn’t published',
  ONLINE_BOOKING: 'Online booking is off',
};

/** The Book online button of a salon whose bookings live on its POS waits for the POS booking link, not for Hayven booking. */
export function isPosBookingLinkWaiting(
  kind: DmButtonSalonPageKind,
  page: { opensNow: boolean } | undefined,
  bookingsOnPos: boolean
): boolean {
  return bookingsOnPos && kind === 'ONLINE_BOOKING' && page !== undefined && !page.opensNow;
}

/**
 * Plain reason a salon page can't be a button; on the POS the booking page needs the POS booking link. `first: false`
 * drops the trailing "first" for a sentence that goes on ("…, or pick another page").
 */
export function salonPageUnavailableMessage(
  kind: DmButtonSalonPageKind,
  reason: DmButtonSalonPageUnavailableReason,
  bookingsOnPos: boolean,
  { first = true }: { first?: boolean } = {}
): string {
  if (bookingsOnPos && kind === 'ONLINE_BOOKING' && reason === 'BOOKING_LINK_UNAVAILABLE') {
    return first ? 'Add your POS booking link first' : 'Add your POS booking link';
  }
  return DM_BUTTON_SALON_PAGE_UNAVAILABLE_MESSAGES[reason];
}

/**
 * Under a salon-page DM button whose page is down today: "Your New Client Offer is paused, so this button opens your
 * Bio Page for now." When the page can't be a button at all (Bio Page unpublished since), saving is refused until the
 * page is back or another one is picked, so the note says that too. A Book online button with no booking link is left
 * out of new DMs (its saved link may be a booking page that takes no bookings). Null while the page opens (or is
 * still loading). With bookings on the POS, Book online waits for the POS booking link ("Add your POS booking link — …").
 */
export function salonPageDownNote(
  kind: DmButtonSalonPageKind,
  page:
    | { opensNow: boolean; fallbackTo: DmButtonFallback | null; unavailableReason: DmButtonSalonPageUnavailableReason | null }
    | undefined,
  bookingsOnPos = false
): string | null {
  if (!page || page.opensNow) return null;
  const leftOut = kind === 'ONLINE_BOOKING' && page.fallbackTo === null;
  const opens =
    page.fallbackTo === 'BIO_PAGE' ? 'your Bio Page' : page.fallbackTo === 'BOOKING' ? 'your booking link' : 'the link saved with it';
  const meanwhile = leftOut ? 'this button is left out of new DMs' : `this button opens ${opens}`;
  if (page.unavailableReason) {
    const reason = salonPageUnavailableMessage(kind, page.unavailableReason, bookingsOnPos, { first: false });
    return `${reason}, or pick another page, to save changes. Until then ${meanwhile}.`;
  }
  if (isPosBookingLinkWaiting(kind, page, bookingsOnPos)) return `Add your POS booking link — ${meanwhile} until then.`;
  return `${PAGE_DOWN_WORDS[kind]}, so ${meanwhile} for now.`;
}
